import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTruck,
  faCalendar,
  faPhone,
  faEnvelope,
  faDollarSign,
  faComments,
  faUsers,
  faChevronLeft,
  faChevronRight,
  faSearch,
  faHandshakeSlash,
} from '@fortawesome/free-solid-svg-icons';
import { supabase } from '../lib/supabaseClient';

const BidsModal = ({
  isOpen,
  onClose,
  loadId,
  bids = [],
  onAcceptBid,
  handleAssignDriver,
}) => {
  const [activeTab, setActiveTab] = useState('bids');
  const [drivers, setDrivers] = useState([]);
  const [operationAuths, setOperationAuths] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentBidPage, setCurrentBidPage] = useState(1);
  const [currentDriverPage, setCurrentDriverPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredDrivers, setFilteredDrivers] = useState([]);
  const [counterOffersHistory, setCounterOffersHistory] = useState({});
  const [newCounterOfferAmounts, setNewCounterOfferAmounts] = useState({});
  const [counterOfferCurrencies, setCounterOfferCurrencies] = useState({});
  const [showCounterOffer, setShowCounterOffer] = useState({});
  const [stopNegotiation, setStopNegotiation] = useState({});
  const [user, setUser] = useState(null);
  const [driverOtherBidsCount, setDriverOtherBidsCount] = useState({});
  const [filteredOperationAuths, setFilteredOperationAuths] = useState([]);
  const [currentOperationAuthPage, setCurrentOperationAuthPage] = useState(1);

  const fetchCounterOffers = async () => {
    try {
      const bidIds = bids.map((bid) => bid.id);
      const { data, error } = await supabase
        .from('counter_offers')
        .select('*')
        .in('bid_id', bidIds)
        .order('created_at', { ascending: true });

      if (error) throw error;

      // Group counter offers by bid_id
      const counterOffersByBid = {};
      data.forEach((offer) => {
        if (!counterOffersByBid[offer.bid_id]) {
          counterOffersByBid[offer.bid_id] = [];
        }
        counterOffersByBid[offer.bid_id].push(offer);
      });

      setCounterOffersHistory(counterOffersByBid);

      // Initialize counter offer amounts and currencies based on the latest counter offer
      const initialAmounts = {};
      const initialCurrencies = {};
      Object.entries(counterOffersByBid).forEach(([bidId, offers]) => {
        if (offers.length > 0) {
          const latestOffer = offers[offers.length - 1];
          initialAmounts[bidId] = latestOffer.amount;
          initialCurrencies[bidId] = latestOffer.currency;
        }
      });

      setNewCounterOfferAmounts((prev) => ({ ...prev, ...initialAmounts }));
      setCounterOfferCurrencies((prev) => ({ ...prev, ...initialCurrencies }));
    } catch (error) {
      console.error('Error fetching counter offers:', error);
    }
  };

  const fetchDriverOtherBidsCount = async () => {
    try {
      // Get all driver IDs from the current bids
      const driverIds = bids.map((bid) => bid.driver_info.id);

      // For each driver, count how many other active bids they have
      const counts = {};

      for (const driverId of driverIds) {
        // Count bids for this driver on other loads (not the current loadId)
        const { count, error } = await supabase
          .from('bids')
          .select('*', { count: 'exact', head: false })
          .eq('driver_id', driverId)
          .neq('load_id', loadId)
          .in('status', ['pending', 'countered']);

        if (error) throw error;

        counts[driverId] = count || 0;
      }

      setDriverOtherBidsCount(counts);
    } catch (error) {
      console.error('Error fetching driver other bids count:', error);
    }
  };

  const fetchOperationAuths = async () => {
    try {
      setLoading(true);

      const { data: allOperationAuths, error: operationAuthsError } =
        await supabase.from('operation_auths').select('*');

      if (operationAuthsError) throw operationAuthsError;

      setOperationAuths(allOperationAuths || []);
    } catch (error) {
      console.error('Error fetching operation authorities:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && loadId) {
      fetchCounterOffers();
      fetchDrivers();
      fetchDriverOtherBidsCount();
      fetchOperationAuths();
    }
  }, [isOpen, loadId, bids]);

  useEffect(() => {
    if (isOpen) {
      if (activeTab === 'drivers') {
        fetchDrivers();
      } else if (activeTab === 'operationAuths') {
        fetchOperationAuths();
      }
    }
  }, [isOpen, activeTab]);

  useEffect(() => {
    if (!isOpen) {
      setCurrentDriverPage(1);
      setCurrentBidPage(1);
      setCurrentOperationAuthPage(1);
    }
  }, [isOpen]);

  useEffect(() => {
    setCurrentDriverPage(1);
    setCurrentBidPage(1);
    setCurrentOperationAuthPage(1);
  }, [activeTab]);

  useEffect(() => {
    if (drivers.length > 0) {
      const filtered = drivers.filter((driver) => {
        const searchStr = searchQuery.toLowerCase();
        const fullName =
          `${driver.first_name} ${driver.last_name}`.toLowerCase();
        return (
          fullName.includes(searchStr) ||
          (driver.email && driver.email.toLowerCase().includes(searchStr)) ||
          (driver.phone && driver.phone.toLowerCase().includes(searchStr)) ||
          (driver.tow_plate &&
            driver.tow_plate.toLowerCase().includes(searchStr)) ||
          (driver.trailer_plate &&
            driver.trailer_plate.toLowerCase().includes(searchStr))
        );
      });
      setFilteredDrivers(filtered);
    }
  }, [searchQuery, drivers]);

  useEffect(() => {
    if (operationAuths.length > 0) {
      const filtered = operationAuths.filter((auth) => {
        const searchStr = searchQuery.toLowerCase();
        const fullName = `${auth.first_name} ${auth.last_name}`.toLowerCase();
        return (
          fullName.includes(searchStr) ||
          (auth.email && auth.email.toLowerCase().includes(searchStr)) ||
          (auth.phone && auth.phone.toLowerCase().includes(searchStr))
        );
      });
      setFilteredOperationAuths(filtered);
    }
  }, [searchQuery, operationAuths]);

  const fetchDrivers = async () => {
    try {
      setLoading(true);

      // First get all approved drivers
      const { data: allDrivers, error: driversError } = await supabase
        .from('profiles')
        .select('*')
        .in('user_type', ['driver', 'company_driver'])
        .eq('status', 'approved');

      if (driversError) throw driversError;

      // Get all active loads with assigned drivers

      // Filter out drivers who are already assigned to active loads

      setDrivers(allDrivers);
    } catch (error) {
      console.error('Error fetching drivers:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const getCurrentUser = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      setUser(user);
    };
    getCurrentUser();
  }, []);

  console.log(loadId);

  if (!isOpen) return null;

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  };

  const getBidStatusClass = (status) => {
    switch (status) {
      case 'accepted':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      case 'pending':
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  const handleAcceptBid = async (bid) => {
    try {
      await onAcceptBid(bid);

      // Get load details
      const { data: loads } = await supabase
        .from('loads')
        .select('*')
        .eq('id', loadId)
        .single();

      // Send push notification to the accepted driver
      try {
        const { data: tokens } = await supabase
          .from('profiles')
          .select('push_token')
          .eq('id', bid.driver_id)
          .single();

        if (tokens?.push_token) {
          const message = {
            to: tokens.push_token,
            sound: 'default',
            title: 'Teklif Kabul Edildi',
            body: `${loads.formatted_id} referanslı yük için teklifiniz kabul edildi.`,
            data: { loadId: loadId, bidId: bid.id },
          };

          await fetch('https://exp.host/--/api/v2/push/send', {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Accept-encoding': 'gzip, deflate',
              'Content-Type': 'application/json',
            },
            mode: 'no-cors',
            body: JSON.stringify(message),
          });
        }

        // Send notifications to other drivers who placed bids
        // Get all other bids for this load
        const otherBids = bids.filter((otherBid) => otherBid.id !== bid.id);

        if (otherBids.length > 0) {
          // Get unique driver IDs from other bids
          const otherDriverIds = [
            ...new Set(otherBids.map((otherBid) => otherBid.driver_id)),
          ];

          // Fetch push tokens for all other drivers
          const { data: otherDriverTokens } = await supabase
            .from('profiles')
            .select('id, push_token')
            .in('id', otherDriverIds);

          // Send notifications to each driver with a valid push token
          const notificationPromises = otherDriverTokens
            .filter((driver) => driver.push_token)
            .map((driver) => {
              const message = {
                to: driver.push_token,
                sound: 'default',
                title: 'Teklif Kapatıldı',
                body: `${loads.formatted_id} referanslı yük için verdiğiniz teklif kapatıldı. Başka bir sürücünün teklifi kabul edildi.`,
                data: { loadId: loadId, status: 'closed' },
              };

              return fetch('https://exp.host/--/api/v2/push/send', {
                method: 'POST',
                headers: {
                  Accept: 'application/json',
                  'Accept-encoding': 'gzip, deflate',
                  'Content-Type': 'application/json',
                },
                mode: 'no-cors',
                body: JSON.stringify(message),
              });
            });

          // Wait for all notifications to be sent
          await Promise.all(notificationPromises);
        }
      } catch (notificationError) {
        console.error('Error sending notification:', notificationError);
      }

      onClose();
    } catch (error) {
      console.error('Error accepting bid:', error);
    }
  };

  const handleRejectBid = async (bid) => {
    try {
      // Update bid status to rejected
      const { error: bidError } = await supabase
        .from('bids')
        .update({ status: 'rejected' })
        .eq('id', bid.id);

      if (bidError) throw bidError;

      // Send push notification to the driver
      try {
        const { data: loads } = await supabase
          .from('loads')
          .select('*')
          .eq('id', loadId)
          .single();

        const { data: tokens } = await supabase
          .from('profiles')
          .select('push_token')
          .eq('id', bid.driver_info.id)
          .single();

        if (tokens?.push_token) {
          const message = {
            to: tokens.push_token,
            sound: 'default',
            title: 'Teklif Reddedildi',
            body: `${loads.formatted_id} referanslı yük için teklifiniz reddedildi.`,
            data: { loadId: loadId, bidId: bid.id },
          };

          await fetch('https://exp.host/--/api/v2/push/send', {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Accept-encoding': 'gzip, deflate',
              'Content-Type': 'application/json',
            },
            mode: 'no-cors',
            body: JSON.stringify(message),
          });
        }
      } catch (notificationError) {
        console.error('Error sending notification:', notificationError);
      }

      // Refresh the bids list
      await fetchCounterOffers();
    } catch (error) {
      console.error('Error rejecting bid:', error);
    }
  };

  const handleCounterOffer = async (bid, amount, stopNegotiating) => {
    try {
      if (!user) {
        console.error('No user found');
        return;
      }

      const { data, error } = await supabase.from('counter_offers').insert({
        bid_id: bid.id,
        amount: parseFloat(amount),
        currency: counterOfferCurrencies[bid.id] || 'EUR',
        stop_negotiation: stopNegotiating,
        created_by: user.id,
      });

      if (error) throw error;

      // Send push notification to the driver
      try {
        const { data: loads } = await supabase
          .from('loads')
          .select('*')
          .eq('id', loadId)
          .single();
        console.error(loadId);

        const { data: tokens } = await supabase
          .from('profiles')
          .select('push_token')
          .eq('id', bid.driver_info.id)
          .single();

        if (tokens?.push_token) {
          const message = {
            to: tokens.push_token,
            sound: 'default',
            title: 'Yeni Karşı Teklif',
            body: `${loads.formatted_id} referanslı yük için ${amount} ${counterOfferCurrencies[bid.id] || 'EUR'} tutarında karşı teklif aldınız.`,
            data: { loadId: loadId, bidId: bid.id },
          };

          await fetch('https://exp.host/--/api/v2/push/send', {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Accept-encoding': 'gzip, deflate',
              'Content-Type': 'application/json',
            },
            mode: 'no-cors',
            body: JSON.stringify(message),
          });
        }
      } catch (notificationError) {
        console.error('Error sending notification:', notificationError);
      }

      // Reset the UI state after successful submission
      setShowCounterOffer((prev) => ({ ...prev, [bid.id]: false }));
      setNewCounterOfferAmounts((prev) => ({ ...prev, [bid.id]: '' }));
      // Para birimini resetleme - eğer önceki teklifler varsa para birimi sabit kalmalı
      // setCounterOfferCurrencies((prev) => ({ ...prev, [bid.id]: 'EUR' }));
      setStopNegotiation((prev) => ({ ...prev, [bid.id]: false }));

      // Fetch updated counter offers
      await fetchCounterOffers();
    } catch (error) {
      console.error('Error submitting counteroffer:', error);
    }
  };

  const handleAssignOperationAuth = async (operationAuth) => {
    try {
      // Update the load with the selected operation authority
      const { error } = await supabase
        .from('loads')
        .update({ operation_auth_id: operationAuth.id })
        .eq('id', loadId);

      if (error) throw error;

      // Send notification to the operation authority if needed

      onClose();
    } catch (error) {
      console.error('Error assigning operation authority:', error);
    }
  };

  const paginateItems = (items, currentPage) => {
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    return items.slice(indexOfFirstItem, indexOfLastItem);
  };

  const renderTabButtons = () => (
    <div className="flex space-x-4 mb-6 border-b">
      <button
        className={`pb-2 px-4 ${
          activeTab === 'bids'
            ? 'border-b-2 border-blue-500 text-blue-600 font-medium'
            : 'text-gray-500 hover:text-gray-700'
        }`}
        onClick={() => setActiveTab('bids')}
      >
        <FontAwesomeIcon icon={faTruck} className="mr-2" />
        Teklifler
      </button>
      <button
        className={`pb-2 px-4 ${
          activeTab === 'drivers'
            ? 'border-b-2 border-blue-500 text-blue-600 font-medium'
            : 'text-gray-500 hover:text-gray-700'
        }`}
        onClick={() => setActiveTab('drivers')}
      >
        <FontAwesomeIcon icon={faUsers} className="mr-2" />
        Sürücüler
      </button>
      <button
        className={`pb-2 px-4 ${
          activeTab === 'operationAuths'
            ? 'border-b-2 border-blue-500 text-blue-600 font-medium'
            : 'text-gray-500 hover:text-gray-700'
        }`}
        onClick={() => setActiveTab('operationAuths')}
      >
        <FontAwesomeIcon icon={faUsers} className="mr-2" />
        Operasyon Yetkilileri
      </button>
    </div>
  );

  const renderDrivers = () => {
    const totalPages = Math.ceil(filteredDrivers.length / itemsPerPage);
    const paginatedDrivers = paginateItems(filteredDrivers, currentDriverPage);

    return (
      <div className="space-y-4 ">
        <div className="relative">
          <input
            type="text"
            placeholder="İsim, email, telefon veya plaka ile ara..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-4 py-2 pl-10 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <FontAwesomeIcon
            icon={faSearch}
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
          />
        </div>

        <div className="max-h-[50vh] overflow-y-auto pr-2">
          {loading ? (
            <div className="text-center py-8">
              <div className="text-gray-400 text-xl mb-4">
                Sürücüler yükleniyor...
              </div>
            </div>
          ) : filteredDrivers.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-gray-400 text-6xl mb-4">
                <FontAwesomeIcon icon={faUsers} />
              </div>
              <p className="text-gray-500 text-lg">
                {searchQuery
                  ? 'Arama kriterlerine uygun sürücü bulunamadı'
                  : 'Herhangi bir sürücü bulunmamaktadır'}
              </p>
            </div>
          ) : (
            paginatedDrivers.map((driver) => (
              <div
                key={driver.id}
                className="border rounded-lg p-4 hover:shadow-md transition-shadow duration-200 mb-2"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mr-3">
                      <FontAwesomeIcon
                        icon={faUsers}
                        className="text-gray-600 text-xl"
                      />
                    </div>
                    <div>
                      <h4 className="font-semibold text-lg">
                        {driver.first_name} {driver.last_name}
                      </h4>
                      <p className="text-sm text-gray-500">
                        <span className="font-bold">
                          {driver.tow_plate} / {driver.trailer_plate}
                        </span>
                      </p>
                      <p className="text-sm text-gray-500">
                        {driver.phone_number}
                      </p>
                    </div>
                  </div>
                  <button
                    className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm"
                    onClick={() => handleAssignDriver(driver)}
                  >
                    Atama
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-4 mt-4 text-sm">
                  {driver.phone && (
                    <div className="flex items-center text-gray-600">
                      <FontAwesomeIcon icon={faPhone} className="mr-2" />
                      {driver.phone}
                    </div>
                  )}
                  {driver.email && (
                    <div className="flex items-center text-gray-600">
                      <FontAwesomeIcon icon={faEnvelope} className="mr-2" />
                      {driver.email}
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        <div className="flex justify-center items-center space-x-2 mt-4 border-t pt-4">
          <button
            onClick={() =>
              setCurrentDriverPage((prev) => Math.max(prev - 1, 1))
            }
            disabled={currentDriverPage === 1}
            className="px-3 py-1 rounded border disabled:bg-gray-100 disabled:text-gray-400"
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>

          <span className="text-sm text-gray-600">
            Sayfa {currentDriverPage} / {totalPages}
          </span>

          <button
            onClick={() =>
              setCurrentDriverPage((prev) => Math.min(prev + 1, totalPages))
            }
            disabled={currentDriverPage === totalPages}
            className="px-3 py-1 rounded border disabled:bg-gray-100 disabled:text-gray-400"
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        </div>
      </div>
    );
  };

  const renderOperationAuths = () => {
    const totalPages = Math.ceil(filteredOperationAuths.length / itemsPerPage);
    const paginatedOperationAuths = paginateItems(
      filteredOperationAuths,
      currentOperationAuthPage
    );

    return (
      <div className="space-y-4 ">
        <div className="relative">
          <input
            type="text"
            placeholder="İsim, email veya telefon ile ara..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-4 py-2 pl-10 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <FontAwesomeIcon
            icon={faSearch}
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
          />
        </div>

        <div className="max-h-[50vh] overflow-y-auto pr-2">
          {loading ? (
            <div className="text-center py-8">
              <div className="text-gray-400 text-xl mb-4">
                Operasyon yetkilileri yükleniyor...
              </div>
            </div>
          ) : filteredOperationAuths.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-gray-400 text-6xl mb-4">
                <FontAwesomeIcon icon={faUsers} />
              </div>
              <p className="text-gray-500 text-lg">
                {searchQuery
                  ? 'Arama kriterlerine uygun operasyon yetkilisi bulunamadı'
                  : 'Herhangi bir operasyon yetkilisi bulunmamaktadır'}
              </p>
            </div>
          ) : (
            paginatedOperationAuths.map((auth) => (
              <div
                key={auth.id}
                className="border rounded-lg p-4 hover:shadow-md transition-shadow duration-200 mb-2"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mr-3">
                      <FontAwesomeIcon
                        icon={faUsers}
                        className="text-gray-600 text-xl"
                      />
                    </div>
                    <div>
                      <h4 className="font-semibold text-lg">
                        {auth.name} {auth.surname}
                      </h4>
                      <p className="text-sm text-gray-500">{auth.phone}</p>
                    </div>
                  </div>
                  <button
                    className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm"
                    onClick={() => handleAssignOperationAuth(auth)}
                  >
                    Atama
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-4 mt-4 text-sm">
                  {auth.phone && (
                    <div className="flex items-center text-gray-600">
                      <FontAwesomeIcon icon={faPhone} className="mr-2" />
                      {auth.phone}
                    </div>
                  )}
                  {auth.email && (
                    <div className="flex items-center text-gray-600">
                      <FontAwesomeIcon icon={faEnvelope} className="mr-2" />
                      {auth.email}
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        <div className="flex justify-center items-center space-x-2 mt-4 border-t pt-4">
          <button
            onClick={() =>
              setCurrentOperationAuthPage((prev) => Math.max(prev - 1, 1))
            }
            disabled={currentOperationAuthPage === 1}
            className="px-3 py-1 rounded border disabled:bg-gray-100 disabled:text-gray-400"
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>

          <span className="text-sm text-gray-600">
            Sayfa {currentOperationAuthPage} / {totalPages}
          </span>

          <button
            onClick={() =>
              setCurrentOperationAuthPage((prev) =>
                Math.min(prev + 1, totalPages)
              )
            }
            disabled={currentOperationAuthPage === totalPages}
            className="px-3 py-1 rounded border disabled:bg-gray-100 disabled:text-gray-400"
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-[600px] shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-2xl font-bold text-gray-800">Load #{loadId}</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500 text-2xl font-semibold"
          >
            ×
          </button>
        </div>

        {renderTabButtons()}

        {activeTab === 'bids' ? (
          <div className="space-y-4">
            <div className="max-h-[50vh] overflow-y-auto pr-2">
              {bids.length === 0 ? (
                <div className="text-center py-8">
                  <div className="text-gray-400 text-6xl mb-4">
                    <FontAwesomeIcon icon={faTruck} />
                  </div>
                  <p className="text-gray-500 text-lg">
                    Bu yük için henüz teklif verilmemiştir
                  </p>
                </div>
              ) : (
                paginateItems(bids, currentBidPage).map((bid) => (
                  <div
                    key={bid.id}
                    className="border rounded-lg p-4 hover:shadow-md transition-shadow duration-200"
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex items-center">
                        <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                          <FontAwesomeIcon
                            icon={faTruck}
                            className="text-blue-600 text-xl"
                          />
                        </div>
                        <div>
                          <h4 className="font-semibold text-lg">
                            {bid.driver_info.first_name} (
                            {bid.bid_type === 'company' ? 'Şirket' : 'Sürücü'})
                          </h4>
                          <span
                            className={`text-sm px-2 py-1 rounded-full ${getBidStatusClass(bid.status)}`}
                          >
                            {bid.status || 'pending'}
                          </span>
                          {driverOtherBidsCount[bid.driver_info.id] > 0 && (
                            <div className="mt-1 text-xs text-orange-600">
                              <FontAwesomeIcon
                                icon={faTruck}
                                className="mr-1"
                              />
                              Bu sürücü{' '}
                              {driverOtherBidsCount[bid.driver_info.id]} adet
                              başka işle daha ilgileniyor
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-2xl font-bold text-green-600">
                          {bid.amount} {bid.currency}
                        </div>
                        <p className="text-sm text-gray-500">
                          <FontAwesomeIcon icon={faCalendar} className="mr-1" />
                          {formatDate(bid.created_at)}
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 mt-4 text-sm">
                      {bid.driver_info.phone_number && (
                        <div className="flex items-center text-gray-600">
                          <FontAwesomeIcon icon={faPhone} className="mr-2" />
                          {bid.driver_info.phone_number}
                        </div>
                      )}
                      {bid.driver_info.email && (
                        <div className="flex items-center text-gray-600">
                          <FontAwesomeIcon icon={faEnvelope} className="mr-2" />
                          {bid.driver_info.email}
                        </div>
                      )}
                    </div>

                    {bid.notes && (
                      <div className="mt-3 text-gray-600 text-sm">
                        <div className="flex items-center mb-1">
                          <FontAwesomeIcon icon={faComments} className="mr-2" />
                          <span className="font-medium">Notes:</span>
                        </div>
                        <p className="ml-6">{bid.notes}</p>
                      </div>
                    )}

                    {counterOffersHistory[bid.id] &&
                      counterOffersHistory[bid.id].length > 0 && (
                        <div className="mt-3 border-t pt-3">
                          <div className="text-sm font-medium text-gray-700 mb-2">
                            <FontAwesomeIcon
                              icon={faDollarSign}
                              className="mr-2"
                            />
                            Karşı Teklifler:
                          </div>
                          <div className="space-y-2">
                            {counterOffersHistory[bid.id].map(
                              (offer, index) => {
                                const isDriverOffer =
                                  offer.created_by === bid.driver_info.id;
                                return (
                                  <div
                                    key={index}
                                    className={`flex justify-between items-center text-sm p-2 rounded ${
                                      isDriverOffer
                                        ? 'bg-blue-50 border border-blue-100'
                                        : 'bg-gray-50 border border-gray-100'
                                    }`}
                                  >
                                    <div className="flex items-center">
                                      <span className="font-medium">
                                        {offer.currency} {offer.amount}
                                      </span>
                                      {offer.stop_negotiation && (
                                        <span className="ml-2 text-xs bg-red-100 text-red-600 px-2 py-1 rounded">
                                          Son Teklif
                                        </span>
                                      )}
                                      <span
                                        className={`ml-2 text-xs px-2 py-1 rounded ${
                                          isDriverOffer
                                            ? 'bg-blue-100 text-blue-600'
                                            : 'bg-gray-100 text-gray-600'
                                        }`}
                                      >
                                        {isDriverOffer
                                          ? `Sürücü Teklifi (${bid.driver_info.first_name} ${bid.driver_info.last_name})`
                                          : 'Yük Sahibi Teklifi'}
                                      </span>
                                    </div>
                                    <span className="text-gray-500 text-xs">
                                      {formatDate(offer.created_at)}
                                    </span>
                                  </div>
                                );
                              }
                            )}
                          </div>
                        </div>
                      )}

                    <div className="mt-4 flex justify-end space-x-2">
                      {showCounterOffer[bid.id] ? (
                        <div className="flex-1 flex items-center space-x-2">
                          <div className="flex-1">
                            <div className="flex space-x-2">
                              <input
                                type="number"
                                value={newCounterOfferAmounts[bid.id] || ''}
                                onChange={(e) =>
                                  setNewCounterOfferAmounts((prev) => ({
                                    ...prev,
                                    [bid.id]: e.target.value,
                                  }))
                                }
                                className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:outline-none"
                                placeholder="Teklif miktarını giriniz"
                              />
                              <select
                                value={counterOfferCurrencies[bid.id] || 'EUR'}
                                onChange={(e) =>
                                  setCounterOfferCurrencies((prev) => ({
                                    ...prev,
                                    [bid.id]: e.target.value,
                                  }))
                                }
                                className="px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:outline-none bg-white"
                                disabled={
                                  counterOffersHistory[bid.id] &&
                                  counterOffersHistory[bid.id].length > 0
                                }
                              >
                                <option value="EUR">€</option>
                                <option value="USD">$</option>
                                <option value="TRY">₺</option>
                              </select>
                            </div>
                            <div className="mt-2 flex items-center">
                              <input
                                type="checkbox"
                                id={`stopNegotiation-${bid.id}`}
                                checked={stopNegotiation[bid.id] || false}
                                onChange={(e) =>
                                  setStopNegotiation((prev) => ({
                                    ...prev,
                                    [bid.id]: e.target.checked,
                                  }))
                                }
                                className="mr-2"
                              />
                              <label
                                htmlFor={`stopNegotiation-${bid.id}`}
                                className="text-sm text-gray-600 flex items-center"
                              >
                                <FontAwesomeIcon
                                  icon={faHandshakeSlash}
                                  className="mr-1"
                                />
                                Pazarlığı Bitir
                              </label>
                            </div>
                          </div>
                          <button
                            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm whitespace-nowrap"
                            onClick={() =>
                              handleCounterOffer(
                                bid,
                                newCounterOfferAmounts[bid.id],
                                stopNegotiation[bid.id]
                              )
                            }
                          >
                            Teklif Gönder
                          </button>
                          <button
                            className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 text-sm"
                            onClick={() =>
                              setShowCounterOffer((prev) => ({
                                ...prev,
                                [bid.id]: false,
                              }))
                            }
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <>
                          <button
                            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 text-sm"
                            onClick={() =>
                              setShowCounterOffer((prev) => ({
                                ...prev,
                                [bid.id]: true,
                              }))
                            }
                            disabled={
                              bid.status === 'accepted' ||
                              bid.status === 'rejected'
                            }
                          >
                            Karşı Teklif Gönder
                          </button>
                          <button
                            className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 text-sm"
                            onClick={() => handleAcceptBid(bid)}
                            disabled={
                              bid.status === 'accepted' ||
                              bid.status === 'rejected'
                            }
                          >
                            Teklifi Kabul Et
                          </button>
                          <button
                            className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 text-sm"
                            onClick={() => handleRejectBid(bid)}
                          >
                            Teklifi Reddet
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>

            {bids.length > itemsPerPage && (
              <div className="flex justify-center items-center space-x-2 mt-4 border-t pt-4">
                <button
                  onClick={() =>
                    setCurrentBidPage((prev) => Math.max(prev - 1, 1))
                  }
                  disabled={currentBidPage === 1}
                  className="px-3 py-1 rounded border disabled:bg-gray-100 disabled:text-gray-400"
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                </button>

                <span className="text-sm text-gray-600">
                  Sayfa {currentBidPage} /{' '}
                  {Math.ceil(bids.length / itemsPerPage)}
                </span>

                <button
                  onClick={() =>
                    setCurrentBidPage((prev) =>
                      Math.min(prev + 1, Math.ceil(bids.length / itemsPerPage))
                    )
                  }
                  disabled={
                    currentBidPage === Math.ceil(bids.length / itemsPerPage)
                  }
                  className="px-3 py-1 rounded border disabled:bg-gray-100 disabled:text-gray-400"
                >
                  <FontAwesomeIcon icon={faChevronRight} />
                </button>
              </div>
            )}
          </div>
        ) : activeTab === 'drivers' ? (
          renderDrivers()
        ) : (
          renderOperationAuths()
        )}

        <div className="mt-6 flex justify-end border-t pt-4">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors duration-200"
          >
            Kapat
          </button>
        </div>
      </div>
    </div>
  );
};

export default BidsModal;
